import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { isString } from "../../utility";
import AuthService from "../../services/authService";

interface ProfileState {
  accountType: "individual" | "cooperate" | undefined;
  setAccountType: (value: "individual" | "cooperate") => void;
  selectedTab: OnboardingTab;
  setTab: (value: OnboardingTab) => void;
  stepNumber: number;
  totalStep: number;
  setStepNumber: (value: number) => void;
  setTotalSteps: (value: number) => void;
  onBoardingPage: OnboardingSteps;
  setOnBoardingPage: (value: OnboardingSteps) => void;
  clearProfileState: () => void;
}

type OnboardingTab =
  | "Create"
  | "Photos"
  | "Address"
  | "Features"
  | "Pricing"
  | "Publish";
type OnboardingSteps =
  | "Create"
  | "Photos"
  | "Address"
  | "Pricing"
  | "Features"
  | "Publish";

export type ProfileDetail = {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  sex: string;
  address: string;
};

export type registerUser = {
  address: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
};

export const useOnboardingStore = create(
  persist<ProfileState>(
    (set) => ({
      accountType: undefined,
      setAccountType: (value) => set(() => ({ accountType: value })),
      selectedTab: "Create",
      setTab: (value) => set(() => ({ selectedTab: value })),
      stepNumber: 1,
      setStepNumber: (value) => set(() => ({ stepNumber: value })),
      setTotalSteps: (value) => set(() => ({ totalStep: value })),
      totalStep: 6,
      onBoardingPage: "Create",
      setOnBoardingPage: (value: OnboardingSteps) =>
        set(() => ({ onBoardingPage: value })),
      clearProfileState: () =>
        set(() => ({
          accountType: undefined,
          selectedTab: "Create",
          stepNumber: 1,
          totalStep: 6,
          onBoardingPage: "Create",
        })),
    }),

    { name: "onBoardingStorage" }
  )
);

type OnboardingDetailsType = {
  details: Contact | null;
  setDetails: (value: Contact) => void;
  clearDetailsStore: () => void;
};
type Contact = { email: string; phoneNumber: string };

export const useOnboardingDetailsStore = create(
  persist<OnboardingDetailsType>(
    (set) => ({
      details: null,
      setDetails: (value: Contact) => set({ details: value }),
      clearDetailsStore: () => set({ details: null }),
    }),
    { name: "onBoardingDetailsStorage" }
  )
);

interface ForgotPasswordState {
  email: string | undefined;
  setEmail: (value: string | undefined) => void;
  clearForgotPasswordState: () => void;
}

export const useForgotPasswordStore = create(
  persist<ForgotPasswordState>(
    (set) => ({
      email: undefined,
      setEmail: (value) => set(() => ({ email: value })),
      clearForgotPasswordState: () =>
        set(() => ({
          email: undefined,
        })),
    }),
    { name: "forgotPasswordStorage" }
  )
);

interface RegisterStore {
  registerUser: registerUser | undefined | string;
  setRegisterUser: (value: registerUser) => void;
  clearUser: () => void;
}

export const useRegisterStore = create(
  persist<RegisterStore>(
    (set) => ({
      registerUser: (() => {
        const data = sessionStorage.getItem("registerUser");

        if (data) {
          try {
            if (!isString(data)) return undefined;

            const parsedData = JSON.parse(data);

            if (
              parsedData?.state?.registerUser === undefined ||
              parsedData?.state?.registerUser === null ||
              Object.keys(parsedData?.state?.registerUser).length === 0
            ) {
              return undefined;
            }

            const user = AuthService.decryptUserData(
              parsedData?.state?.registerUser
            );

            return user;
          } catch (error) {
            console.log("Error parsing JSON", error);
            return undefined;
          }
        }
        return undefined;
      })(),
      setRegisterUser: (value: registerUser | string) => {
        const data = JSON.stringify(value);
        const encryptedData = AuthService.encryptUserData(data);
        set({ registerUser: encryptedData });
      },
      clearUser: () => {
        sessionStorage.removeItem("registerUser");
        set({ registerUser: undefined });
      },
    }),
    {
      name: "registerUser",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
